import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus, TrendingUp, Pause, Play, X, ChevronRight,
  BarChart2, Image, AlertTriangle, CheckCircle,
} from 'lucide-react'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import {
  Card, MetricCard, Badge, PageHeader, EmptyState,
} from '../components/Card'
import {
  listCampaigns, createCampaign, adsReport, optimizeAds,
  pauseCampaign, resumeCampaign, getCampaignCreatives,
  type Campaign, type OptAction,
} from '../api/client'
import { layout } from '../utils/layout'

const GOALS = ['awareness', 'traffic', 'engagement', 'leads', 'sales']

// Generate synthetic 7-day spend pacing from campaign metrics
function buildSpendChart(campaign: Campaign) {
  const totalSpend = campaign.metrics?.spend ?? 0
  const budget = campaign.daily_budget_usd ?? 50
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date()
    day.setDate(day.getDate() - (6 - i))
    const frac = (i + 1) / 7
    return {
      date: day.toLocaleDateString('en', { weekday: 'short' }),
      spend: parseFloat((totalSpend * frac * (0.85 + Math.random() * 0.3)).toFixed(2)),
      budget: budget,
    }
  })
}

export default function AdsView() {
  const qc = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState({ goal: 'leads', budget: 50, audience: '' })
  const [selected, setSelected] = useState<Campaign | null>(null)
  const [optimizeActions, setOptimizeActions] = useState<OptAction[] | null>(null)
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined)

  const { data: campaigns } = useQuery({
    queryKey: ['campaigns', statusFilter],
    queryFn: () => listCampaigns(statusFilter),
  })
  const { data: report } = useQuery({
    queryKey: ['ads-report'],
    queryFn: () => adsReport('7d'),
  })
  const { data: creatives } = useQuery({
    queryKey: ['creatives', selected?.id],
    queryFn: () => getCampaignCreatives(selected!.id),
    enabled: !!selected,
  })

  const createMut = useMutation({
    mutationFn: () => createCampaign({ goal: form.goal, budget_daily_usd: form.budget, audience_description: form.audience }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['campaigns'] }); setShowCreate(false) },
  })

  const optimizeMut = useMutation({
    mutationFn: () => optimizeAds('moderate'),
    onSuccess: (data) => setOptimizeActions(data.actions),
  })

  const pauseMut = useMutation({
    mutationFn: (id: string) => pauseCampaign(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns'] }),
  })

  const resumeMut = useMutation({
    mutationFn: (id: string) => resumeCampaign(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns'] }),
  })

  const spendData = selected ? buildSpendChart(selected) : []

  return (
    <div>
      <PageHeader
        title="Ads Command Center"
        subtitle="Meta Marketing API v23 · Budget optimization · Creative fatigue detection"
        action={
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 bg-brand-600 hover:bg-brand-700 text-white text-sm px-3 py-1.5 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" /> New Campaign
          </button>
        }
      />

      <div className={`${layout.pagePadding} ${layout.pageStack}`}>
        {/* Key metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard label="Total Spend (7d)" value={`$${(report?.total_spend ?? 0).toFixed(0)}`} />
          <MetricCard label="Impressions" value={(report?.total_impressions ?? 0).toLocaleString()} />
          <MetricCard label="Avg CTR" value={`${(report?.avg_ctr ?? 0).toFixed(2)}%`} />
          <MetricCard label="Avg ROAS" value={`${(report?.avg_roas ?? 0).toFixed(1)}x`} />
        </div>

        {/* Actions row */}
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => optimizeMut.mutate()}
            disabled={optimizeMut.isPending}
            className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-sm px-4 py-2 rounded-lg transition-colors"
          >
            <TrendingUp className="w-4 h-4" />
            {optimizeMut.isPending ? 'Analysing…' : 'Run Budget Optimization'}
          </button>
          {/* Status filter */}
          {(['all', 'active', 'paused', 'completed'] as const).map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s === 'all' ? undefined : s)}
              className={`text-xs px-3 py-1.5 rounded-full capitalize transition-colors ${
                (statusFilter ?? 'all') === s
                  ? 'bg-brand-700 text-brand-100'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Optimization suggestions */}
        {optimizeActions && optimizeActions.length > 0 && (
          <Card title="Optimization Suggestions">
            <div className="space-y-2">
              {optimizeActions.map((a, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-slate-800/40 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sm font-medium text-slate-200">{a.campaign_name}</span>
                      <Badge color="yellow">{a.action}</Badge>
                    </div>
                    <p className="text-xs text-slate-400">{a.reason}</p>
                    {a.old_budget !== a.new_budget && (
                      <p className="text-xs text-slate-500 mt-1">
                        Budget: <span className="line-through text-slate-600">${a.old_budget}/day</span>
                        {' → '}
                        <span className="text-green-400">${a.new_budget}/day</span>
                      </p>
                    )}
                  </div>
                  <button className="shrink-0 text-xs bg-brand-700 hover:bg-brand-600 text-brand-100 px-2 py-1 rounded-md flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Apply
                  </button>
                </div>
              ))}
            </div>
          </Card>
        )}

        <div className="flex gap-4">
          {/* Campaign list */}
          <div className="flex-1 min-w-0">
            <Card title={`Campaigns (${campaigns?.total ?? 0})`}>
              {!campaigns?.campaigns.length ? (
                <EmptyState
                  icon={<Megaphone className="w-12 h-12" />}
                  title="No campaigns yet"
                  description="Create your first campaign from a high-level goal."
                  action={
                    <button onClick={() => setShowCreate(true)} className="bg-brand-600 hover:bg-brand-700 text-white text-sm px-4 py-2 rounded-lg">
                      Create Campaign
                    </button>
                  }
                />
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-slate-500 border-b border-surface-border">
                      <th className="text-left pb-2">Campaign</th>
                      <th className="text-right pb-2">Budget</th>
                      <th className="text-right pb-2">Spend</th>
                      <th className="text-right pb-2">ROAS</th>
                      <th className="text-right pb-2">Status</th>
                      <th className="text-right pb-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.campaigns.map(c => (
                      <tr
                        key={c.id}
                        onClick={() => setSelected(s => s?.id === c.id ? null : c)}
                        className={`border-b border-surface-border/50 cursor-pointer transition-colors ${
                          selected?.id === c.id ? 'bg-brand-900/30' : 'hover:bg-slate-800/30'
                        }`}
                      >
                        <td className="py-2 font-medium">
                          <div className="flex items-center gap-2">
                            <ChevronRight className={`w-3.5 h-3.5 text-slate-500 transition-transform ${selected?.id === c.id ? 'rotate-90' : ''}`} />
                            {c.name}
                          </div>
                        </td>
                        <td className="py-2 text-right">${c.daily_budget_usd}/day</td>
                        <td className="py-2 text-right">${(c.metrics?.spend ?? 0).toFixed(0)}</td>
                        <td className="py-2 text-right">{(c.metrics?.roas ?? 0).toFixed(1)}x</td>
                        <td className="py-2 text-right">
                          <Badge color={c.status === 'active' ? 'green' : c.status === 'paused' ? 'yellow' : 'slate'}>
                            {c.status}
                          </Badge>
                        </td>
                        <td className="py-2 text-right" onClick={e => e.stopPropagation()}>
                          {c.status === 'active' ? (
                            <button
                              onClick={() => pauseMut.mutate(c.id)}
                              disabled={pauseMut.isPending}
                              className="p-1.5 rounded-md hover:bg-slate-700 text-yellow-400 transition-colors"
                              title="Pause"
                            >
                              <Pause className="w-3.5 h-3.5" />
                            </button>
                          ) : (
                            <button
                              onClick={() => resumeMut.mutate(c.id)}
                              disabled={resumeMut.isPending}
                              className="p-1.5 rounded-md hover:bg-slate-700 text-green-400 transition-colors"
                              title="Resume"
                            >
                              <Play className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          </div>

          {/* Campaign detail panel */}
          {selected && (
            <div className="w-80 shrink-0 space-y-4">
              <Card title={selected.name} action={
                <button onClick={() => setSelected(null)} className="p-1 rounded hover:bg-slate-700 text-slate-400">
                  <X className="w-4 h-4" />
                </button>
              }>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-slate-800/60 rounded-lg p-2">
                      <div className="text-slate-500 mb-0.5">Impressions</div>
                      <div className="text-slate-200 font-semibold">{(selected.metrics?.impressions ?? 0).toLocaleString()}</div>
                    </div>
                    <div className="bg-slate-800/60 rounded-lg p-2">
                      <div className="text-slate-500 mb-0.5">CTR</div>
                      <div className="text-slate-200 font-semibold">{(selected.metrics?.ctr ?? 0).toFixed(2)}%</div>
                    </div>
                    <div className="bg-slate-800/60 rounded-lg p-2">
                      <div className="text-slate-500 mb-0.5">Spend</div>
                      <div className="text-slate-200 font-semibold">${(selected.metrics?.spend ?? 0).toFixed(2)}</div>
                    </div>
                    <div className="bg-slate-800/60 rounded-lg p-2">
                      <div className="text-slate-500 mb-0.5">ROAS</div>
                      <div className="text-slate-200 font-semibold">{(selected.metrics?.roas ?? 0).toFixed(1)}x</div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Budget pacing chart */}
              <Card title="Spend Pacing (7d)">
                <div className="h-28">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={spendData} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                      <defs>
                        <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#64748b' }} />
                      <YAxis tick={{ fontSize: 9, fill: '#64748b' }} />
                      <Tooltip
                        contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 11 }}
                        formatter={(v: number) => [`$${v}`, '']}
                      />
                      <Area type="monotone" dataKey="budget" stroke="#334155" strokeDasharray="4 4" fill="none" name="Budget" />
                      <Area type="monotone" dataKey="spend" stroke="#6366f1" strokeWidth={2} fill="url(#spendGrad)" name="Spend" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              {/* Creative gallery */}
              <Card title="Creatives">
                {!creatives?.creatives.length ? (
                  <div className="text-center py-4 text-xs text-slate-500">
                    <Image className="w-8 h-8 mx-auto mb-2 text-slate-700" />
                    No creatives yet
                  </div>
                ) : (
                  <div className="space-y-2">
                    {creatives.creatives.slice(0, 4).map(cr => (
                      <div key={cr.id} className="flex items-center gap-2 text-xs p-2 bg-slate-800/40 rounded-lg">
                        <BarChart2 className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                        <span className="flex-1 text-slate-300 truncate">{cr.name}</span>
                        <Badge color={cr.status === 'active' ? 'green' : 'slate'}>{cr.format}</Badge>
                        {cr.performance_score != null && (
                          <span className={`font-semibold ${cr.performance_score > 0.6 ? 'text-green-400' : cr.performance_score > 0.3 ? 'text-yellow-400' : 'text-red-400'}`}>
                            {(cr.performance_score * 100).toFixed(0)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Create Campaign Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
          <div className="bg-surface-card border border-surface-border rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-4">New Campaign</h2>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Goal</label>
                <select
                  value={form.goal}
                  onChange={e => setForm(f => ({ ...f, goal: e.target.value }))}
                  className="w-full bg-slate-800 border border-surface-border rounded-lg px-3 py-2 text-sm"
                >
                  {GOALS.map(g => <option key={g} value={g}>{g.charAt(0).toUpperCase() + g.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Daily Budget (USD)</label>
                <input
                  type="number"
                  value={form.budget}
                  onChange={e => setForm(f => ({ ...f, budget: +e.target.value }))}
                  className="w-full bg-slate-800 border border-surface-border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Audience Description</label>
                <input
                  type="text"
                  value={form.audience}
                  onChange={e => setForm(f => ({ ...f, audience: e.target.value }))}
                  placeholder="25-45 year old marketing professionals…"
                  className="w-full bg-slate-800 border border-surface-border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={() => setShowCreate(false)} className="flex-1 bg-slate-700 hover:bg-slate-600 text-sm py-2 rounded-lg">Cancel</button>
                <button
                  onClick={() => createMut.mutate()}
                  disabled={createMut.isPending}
                  className="flex-1 bg-brand-600 hover:bg-brand-700 text-white text-sm py-2 rounded-lg"
                >
                  {createMut.isPending ? 'Creating…' : 'Generate Plan'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function Megaphone({ className }: { className?: string }) {
  return <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" /></svg>
}
